import axios from 'axios';
import { API_ROOT } from '../../config/env';
import { tokenStorage } from '../storage/tokenStorage';
import { ApiError, extractFieldErrors } from './ApiError';

// ── Response envelope unwrapping ────────────────────────────────
// The CityCraft API wraps payloads as { responseStatus, result, message }.
// Some endpoints return the value directly (e.g. Status returns a string,
// RefreshAdminDashboard returns `true`). We normalise to the inner payload.
const unwrap = (data) => {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if ('result' in data) return data.result;
    if ('data' in data) return data.data;
  }
  return data;
};

// Callback registered by AuthProvider so a 401 can force logout/redirect.
let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => { onUnauthorized = fn; };

const instance = axios.create({
  baseURL: API_ROOT,
  timeout: 20000,
  // Base of both header sets (Content-Type + Accept). The auth header is
  // added per-request by the interceptor below.
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  // NOTE: withCredentials must stay false — the API responds with
  // `Access-Control-Allow-Origin: *`, which the browser rejects for
  // credentialed (cookie) requests. Auth is bearer-token based.
  withCredentials: false,
});

// The API distinguishes two header sets:
//   • headersWithoutToken — public/unauthenticated endpoints (OTP, login).
//     Pass `{ skipAuth: true }` in the request config to use this set.
//   • headersAPI          — authenticated endpoints (profile, bookings, cart…).
//     Default: attaches the stored JWT as `Authorization: Bearer <token>`.
instance.interceptors.request.use((config) => {
  // Always accept JSON unless a call overrides it (e.g. Status → text/plain).
  if (!config.headers.Accept) config.headers.Accept = 'application/json';

  if (config.skipAuth) {
    // Public endpoint → never send a token.
    delete config.headers.Authorization;
    return config;
  }

  const token = tokenStorage.getToken();
  if (token) {
    // Tolerate a token already stored with the scheme prefix.
    config.headers.Authorization = /^bearer\s/i.test(token) ? token : `${token}`;
  }
  return config;
});   

// Normalise responses + errors.
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status ?? 0;
    const data = error?.response?.data;

    // A 401 from a single request must NOT tear down a persisted session —
    // that caused "reload logs me out". We surface the error to the caller
    // and let AuthProvider decide (it no longer force-clears on 401).
    if (status === 401 && onUnauthorized) onUnauthorized(error?.config?.url);

    const message =
      (data && (data.message || data.title || data.error)) ||
      error.message ||
      'Network error';

    return Promise.reject(
      new ApiError({
        message,
        status,
        code: data?.code ?? data?.responseStatus ?? null,
        fieldErrors: extractFieldErrors(data),
        raw: data,
      }),
    );
  },
);

// BaseApiClient — every feature API class extends this.
// Pass `{ public: true }` for controllers whose endpoints never need a token
// (browse/catalog) — every request then uses headersWithoutToken by default.
// A per-call `{ skipAuth }` in config still wins.
export class BaseApiClient {
  constructor(resourcePath = '', opts = {}) {
    // resourcePath e.g. 'Landing', 'Address' -> requests hit /api/v1/<resource>/...
    this.base = resourcePath ? `/${resourcePath}` : '';
    this._public = !!opts.public;
  }

  _url(path = '') {
    if (!path) return this.base;
    return `${this.base}${path.startsWith('/') ? path : `/${path}`}`;
  }

  // Apply the controller-level public default unless the call set skipAuth itself.
  _cfg(config) {
    if (this._public && (!config || config.skipAuth === undefined)) {
      return { ...(config || {}), skipAuth: true };
    }
    return config;
  }

  async get(path, config) {
    const res = await instance.get(this._url(path), this._cfg(config));
    return unwrap(res.data);
  }

  async post(path, body, config) {
    const res = await instance.post(this._url(path), body, this._cfg(config));
    return unwrap(res.data);
  }

  async put(path, body, config) {
    const res = await instance.put(this._url(path), body, this._cfg(config));
    return unwrap(res.data);
  }

  async delete(path, config) {
    const res = await instance.delete(this._url(path), this._cfg(config));
    return unwrap(res.data);
  }

  // Escape hatch when a caller needs the full envelope, not the unwrapped result.
  async raw(method, path, body, config) {
    const merged = this._cfg(config) || {};
    const res = await instance.request({ method, url: this._url(path), data: body, ...merged });
    return res.data;
  }
}

export { instance as axiosInstance };
